import { useState } from 'react';
import Markdown from 'react-markdown';
import { Link, useParams } from 'react-router-dom';
import type { IBlock } from '@stem/contracts';
import { useAssetUrl } from '../media/useAssetUrl';

export interface BlockAnswer {
  blockId: string;
  correct: boolean;
  score: number;
  maxScore: number;
}

interface BlockProps {
  block: IBlock;
  onAnswered: (answer: BlockAnswer) => void;
  answered?: BlockAnswer;
}

/**
 * One renderer per block type, and one place that switches on it.
 *
 * Graded blocks mark themselves in the browser so a learner gets an answer
 * immediately on a slow connection — and emit a `block_answered` event so the
 * record is the server's. Same split as the lab: instant locally, authoritative
 * remotely, and the two agree because the marking rule is the same object.
 */
export function BlockRenderer({ block, onAnswered, answered }: BlockProps) {
  switch (block.type) {
    case 'rich_text':
      return (
        <div className="prose-lesson">
          <Markdown>{block.markdown}</Markdown>
        </div>
      );

    case 'callout':
      return <Callout block={block} />;

    case 'image':
    case 'video':
    case 'audio':
      return <MediaBlock block={block} />;

    case 'mcq':
      return <McqBlock block={block} onAnswered={onAnswered} answered={answered} />;

    case 'numeric':
      return <NumericBlock block={block} onAnswered={onAnswered} answered={answered} />;

    case 'drag_match':
      return <DragMatchBlock block={block} onAnswered={onAnswered} answered={answered} />;

    case 'reflection':
      return <ReflectionBlock block={block} />;

    case 'lab_embed':
      return <LabEmbedBlock block={block} />;

    case 'download':
      return <DownloadBlock block={block} />;

    default:
      return null;
  }
}

const CALLOUT_STYLES: Record<string, string> = {
  info: 'border-brand-300 bg-brand-50',
  tip: 'border-emerald-300 bg-emerald-50',
  warning: 'border-savanna-400 bg-savanna-400/10',
  vocabulary: 'border-ink-300 bg-ink-100',
};

function Callout({ block }: { block: Extract<IBlock, { type: 'callout' }> }) {
  return (
    <aside className={`rounded-lg border-l-4 p-4 ${CALLOUT_STYLES[block.tone] ?? CALLOUT_STYLES.info}`}>
      {block.title && <p className="mb-1 text-sm font-semibold text-ink-900">{block.title}</p>}
      <div className="prose-lesson text-sm">
        <Markdown>{block.markdown}</Markdown>
      </div>
    </aside>
  );
}

/**
 * Media, with the transcript always available.
 *
 * `preload="none"` on video and audio, always. A lesson page that starts
 * fetching four megabytes because it mounted is a lesson page that costs a
 * learner their airtime before they have read the first paragraph.
 *
 * The URL is signed on demand rather than being a stable path, because the
 * Storage bucket is private — see `useAssetUrl`. Until the signature arrives,
 * and if it never does, the alt text or caption stands in: a lesson whose video
 * has not been uploaded should still read.
 */
function MediaBlock({ block }: { block: Extract<IBlock, { type: 'image' | 'video' | 'audio' }> }) {
  const [failed, setFailed] = useState(false);
  const signed = useAssetUrl(block.assetId);
  const src = signed.data?.url;

  if (!src || signed.isError) {
    return (
      <figure>
        <p className="rounded-lg border border-dashed border-ink-300 p-4 text-sm text-ink-500">
          {signed.isPending
            ? 'Loading media…'
            : (block.alt ?? block.caption ?? 'This media is not available yet.')}
        </p>
        {block.transcript && <p className="mt-2 text-sm text-ink-700">{block.transcript}</p>}
      </figure>
    );
  }

  if (failed || block.type === 'image') {
    return (
      <figure>
        {!failed && block.type === 'image' && (
          <img
            className="w-full rounded-lg border border-ink-200"
            src={src}
            alt={block.alt ?? block.caption ?? ''}
            loading="lazy"
            onError={() => setFailed(true)}
          />
        )}
        {failed && (
          <p className="rounded-lg border border-dashed border-ink-300 p-4 text-sm text-ink-500">
            {block.alt ?? block.caption ?? 'This media is not available yet.'}
          </p>
        )}
        {block.caption && (
          <figcaption className="mt-1 text-xs text-ink-500">{block.caption}</figcaption>
        )}
      </figure>
    );
  }

  return (
    <figure>
      {block.type === 'video' ? (
        <video className="w-full rounded-lg" controls preload="none" onError={() => setFailed(true)}>
          <source src={src} />
        </video>
      ) : (
        <audio className="w-full" controls preload="none" onError={() => setFailed(true)}>
          <source src={src} />
        </audio>
      )}
      {block.caption && <figcaption className="mt-1 text-xs text-ink-500">{block.caption}</figcaption>}
      {block.transcript && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs font-medium text-brand-700">Transcript</summary>
          <p className="mt-1 text-sm text-ink-700">{block.transcript}</p>
        </details>
      )}
    </figure>
  );
}

function McqBlock({
  block,
  onAnswered,
  answered,
}: {
  block: Extract<IBlock, { type: 'mcq' }>;
  onAnswered: (answer: BlockAnswer) => void;
  answered?: BlockAnswer;
}) {
  const [chosen, setChosen] = useState<string[]>([]);
  const multi = block.correct.length > 1;

  const submit = () => {
    const correct =
      chosen.length === block.correct.length && chosen.every(id => block.correct.includes(id));
    onAnswered({
      blockId: block.id,
      correct,
      score: correct ? (block.points ?? 1) : 0,
      maxScore: block.points ?? 1,
    });
  };

  return (
    <div className="card">
      <p className="font-medium text-ink-900">{block.prompt}</p>
      <div className="mt-3 space-y-2">
        {block.options.map(option => (
          <label key={option.id} className="flex items-start gap-2 text-sm">
            <input
              type={multi ? 'checkbox' : 'radio'}
              name={block.id}
              className="mt-1"
              disabled={Boolean(answered)}
              checked={chosen.includes(option.id)}
              onChange={() =>
                setChosen(current =>
                  multi
                    ? current.includes(option.id)
                      ? current.filter(id => id !== option.id)
                      : [...current, option.id]
                    : [option.id],
                )
              }
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>

      {!answered ? (
        <button
          type="button"
          className="btn-primary mt-3"
          disabled={chosen.length === 0}
          onClick={submit}
        >
          Check
        </button>
      ) : (
        <Feedback correct={answered.correct} explanation={block.explanation} />
      )}
    </div>
  );
}

function NumericBlock({
  block,
  onAnswered,
  answered,
}: {
  block: Extract<IBlock, { type: 'numeric' }>;
  onAnswered: (answer: BlockAnswer) => void;
  answered?: BlockAnswer;
}) {
  const [value, setValue] = useState('');

  const submit = () => {
    const numeric = Number(value);
    const correct =
      Number.isFinite(numeric) && Math.abs(numeric - block.answer) <= (block.tolerance ?? 0);
    onAnswered({
      blockId: block.id,
      correct,
      score: correct ? (block.points ?? 1) : 0,
      maxScore: block.points ?? 1,
    });
  };

  return (
    <div className="card">
      <p className="font-medium text-ink-900">{block.prompt}</p>
      <div className="mt-3 flex items-center gap-2">
        <input
          className="input max-w-[10rem]"
          type="number"
          inputMode="numeric"
          value={value}
          disabled={Boolean(answered)}
          onChange={event => setValue(event.target.value)}
        />
        {block.unit && <span className="text-sm text-ink-500">{block.unit}</span>}
        {!answered && (
          <button type="button" className="btn-primary" disabled={value === ''} onClick={submit}>
            Check
          </button>
        )}
      </div>
      {answered && <Feedback correct={answered.correct} explanation={block.explanation} />}
    </div>
  );
}

function DragMatchBlock({
  block,
  onAnswered,
  answered,
}: {
  block: Extract<IBlock, { type: 'drag_match' }>;
  onAnswered: (answer: BlockAnswer) => void;
  answered?: BlockAnswer;
}) {
  const [pairs, setPairs] = useState<Record<string, string>>({});

  const submit = () => {
    const correct = Object.entries(block.pairs).every(([left, right]) => pairs[left] === right);
    onAnswered({
      blockId: block.id,
      correct,
      score: correct ? (block.points ?? 1) : 0,
      maxScore: block.points ?? 1,
    });
  };

  return (
    <div className="card">
      <p className="font-medium text-ink-900">{block.prompt}</p>
      <div className="mt-3 space-y-2">
        {block.left.map(item => (
          <div key={item.id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="min-w-[8rem] font-medium">{item.label}</span>
            {/*
              A select rather than a drag target. Dragging is pleasant with a
              mouse and impossible with a keyboard or a screen reader, and this
              question is about the matching, not about the motor skill.
            */}
            <select
              className="input max-w-xs"
              disabled={Boolean(answered)}
              value={pairs[item.id] ?? ''}
              onChange={event => setPairs(current => ({ ...current, [item.id]: event.target.value }))}
            >
              <option value="">Choose…</option>
              {block.right.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {!answered ? (
        <button
          type="button"
          className="btn-primary mt-3"
          disabled={Object.keys(pairs).length < block.left.length}
          onClick={submit}
        >
          Check
        </button>
      ) : (
        <Feedback correct={answered.correct} />
      )}
    </div>
  );
}

/**
 * A reflection prompt.
 *
 * Saved locally and never sent. Reflection is for the learner: "what would make
 * this hard in real life" is a question you answer honestly only if nobody is
 * marking it, and a platform that quietly uploads it has changed the question.
 */
function ReflectionBlock({ block }: { block: Extract<IBlock, { type: 'reflection' }> }) {
  const key = `stem.reflection.${block.id}`;
  const [text, setText] = useState(() =>
    typeof localStorage === 'undefined' ? '' : (localStorage.getItem(key) ?? ''),
  );
  const words = text.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="card">
      <p className="font-medium text-ink-900">{block.prompt}</p>
      <textarea
        className="input mt-2 min-h-[6rem]"
        value={text}
        onChange={event => {
          setText(event.target.value);
          localStorage.setItem(key, event.target.value);
        }}
      />
      <p className="mt-1 text-xs text-ink-500">
        {words} word{words === 1 ? '' : 's'}
        {block.minWords ? ` · aim for at least ${block.minWords}` : ''} · saved on this device only.
      </p>
    </div>
  );
}

function LabEmbedBlock({ block }: { block: Extract<IBlock, { type: 'lab_embed' }> }) {
  const { orgSlug = '' } = useParams();
  return (
    <div className="card border-brand-300 bg-brand-50">
      <p className="text-sm font-semibold text-brand-900">Virtual laboratory</p>
      <p className="mt-1 text-sm text-ink-700">
        This lesson includes a hands-on laboratory. It opens full screen so the scene and the tables
        have room to work.
      </p>
      <Link to={`/o/${orgSlug}/labs/${block.labSlug}`} className="btn-primary mt-3">
        Open the laboratory
      </Link>
    </div>
  );
}

function DownloadBlock({ block }: { block: Extract<IBlock, { type: 'download' }> }) {
  const signed = useAssetUrl(block.assetId);
  const bytes = block.bytes ?? signed.data?.bytes;

  return (
    <a
      className={`card flex items-center justify-between gap-3 hover:border-brand-400 ${
        signed.data ? '' : 'pointer-events-none opacity-60'
      }`}
      href={signed.data?.url ?? '#'}
      target="_blank"
      rel="noreferrer"
      download
    >
      <span className="text-sm font-medium text-ink-900">{block.label}</span>
      {/* The size is shown before the tap, so a learner on metered data can decide. */}
      <span className="text-xs text-ink-500">
        {bytes ? `${(bytes / 1_048_576).toFixed(1)} MB` : signed.isPending ? 'Preparing…' : 'Download'}
      </span>
    </a>
  );
}

function Feedback({ correct, explanation }: { correct: boolean; explanation?: string }) {
  return (
    <div
      className={`mt-3 rounded-lg p-3 text-sm ${
        correct ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
      }`}
    >
      <p className="font-semibold">{correct ? '✓ Correct' : '✗ Not quite'}</p>
      {explanation && <p className="mt-1">{explanation}</p>}
    </div>
  );
}
